import { Action, Message } from "src/global/messageBroker/types"

export const MAX_BUFFER_SIZE = 100
export const MAX_TOTAL_DELAY = 5000
const DEFAULT_DELAY = 1000

interface BufferedEvent {
  eventType: string
  messages: Message[]
  timeoutId?: number
  /** Track when the first message was added */
  firstMessageTime: number
}

/**
 * Internal safeguard configuration for the EventBuffer
 */
interface SafeguardConfig {
  /** Maximum number of messages in a buffer before forcing processing (default: 100) */
  maxBufferSize: number
  /** Maximum time in milliseconds a buffer can exist before forcing processing (default: 5000ms) */
  maxTotalDelay: number
}

/**
 * Buffers events of the same type and only processes the most recent one
 * after a specified delay, reducing UI load during bursts of events.
 */
export class EventBuffer {
  private buffers: Map<string, BufferedEvent> = new Map()
  private readonly delay: number
  private readonly safeguards: SafeguardConfig
  private readonly action: Action

  /**
   * Creates a new EventBuffer
   * @param action The action to execute for the latest message in a buffer
   * @param delay The delay in milliseconds before processing (default: 1000ms)
   */
  constructor(action: Action, delay: number = DEFAULT_DELAY) {
    this.action = action
    this.delay = delay

    this.safeguards = {
      maxBufferSize: MAX_BUFFER_SIZE,
      maxTotalDelay: MAX_TOTAL_DELAY,
    }
  }

  /**
   * Adds a message to the buffer for its event type
   * @param message The message to buffer
   */
  add(message: Message): void {
    const eventType = message.event
    const now = Date.now()
    const existingBuffer = this.buffers.get(eventType)

    if (existingBuffer) {
      // Add message to existing buffer
      existingBuffer.messages.push(message)

      if (this.shouldProcessImmediately(existingBuffer, now)) {
        if (existingBuffer.timeoutId) {
          window.clearTimeout(existingBuffer.timeoutId)
        }
        this.processBuffer(eventType)
        return
      }

      // Clear existing timeout to reset the window
      if (existingBuffer.timeoutId) {
        window.clearTimeout(existingBuffer.timeoutId)
      }
    } else {
      // Create new buffer
      this.buffers.set(eventType, {
        eventType,
        messages: [message],
        firstMessageTime: now,
      })
    }

    // Set new timeout
    const timeoutId = window.setTimeout(() => {
      this.processBuffer(eventType)
    }, this.delay)

    const buffer = this.buffers.get(eventType)
    if (buffer) {
      this.buffers.set(eventType, {
        ...buffer,
        timeoutId,
      })
    }
  }

  /**
   * Determines if a buffer should be processed immediately based on safeguards
   * @param buffer The buffer to check
   * @param currentTime The current timestamp
   * @returns True if the buffer should be processed immediately
   */
  private shouldProcessImmediately(buffer: BufferedEvent, currentTime: number): boolean {
    // Process if buffer size exceeds maximum
    if (buffer.messages.length >= this.safeguards.maxBufferSize) {
      return true
    }

    // Process if total elapsed time exceeds maximum delay
    if (currentTime - buffer.firstMessageTime >= this.safeguards.maxTotalDelay) {
      return true
    }

    return false
  }

  /**
   * Processes a buffer for a specific event type
   * @param eventType The event type to process
   */
  private processBuffer(eventType: string): void {
    const buffer = this.buffers.get(eventType)
    if (buffer) {
      // Process only the most recent message
      this.action(buffer.messages[buffer.messages.length - 1])
      this.buffers.delete(eventType)
    }
  }

  /**
   * Clears all pending buffers and their timeouts
   */
  clear(): void {
    // Clean up any pending timeouts
    for (const buffer of this.buffers.values()) {
      if (buffer.timeoutId) {
        window.clearTimeout(buffer.timeoutId)
      }
    }
    this.buffers.clear()
  }
}
